import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Users, Shield, Calendar, Mail } from "lucide-react";
import { ParserTrainingPanel } from "@/components/admin/ParserTrainingPanel";
import { EnrichmentDebugPanel } from "@/components/admin/EnrichmentDebugPanel";
import { safeFormat } from "@/lib/formatDateTime";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface UserWithRole {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  role: "user" | "admin";
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isLoading: roleLoading } = useIsAdmin();
  const navigate = useNavigate();

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ["adminUsers"],
    queryFn: async () => {
      // Get all profiles (admins can view all)
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (profilesError) throw profilesError;

      // Get all roles
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("*");
      
      if (rolesError) throw rolesError;

      // Combine data
      return profiles.map((profile) => {
        const userRole = roles.find((r) => r.user_id === profile.user_id);
        return {
          ...profile,
          role: userRole?.role || "user",
        } as UserWithRole;
      });
    },
    enabled: isAdmin,
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate("/trips");
    }
  }, [isAdmin, roleLoading, navigate]);

  if (authLoading || roleLoading || usersLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      
      <main className="flex-1 py-8">
        <div className="container">
          <Link 
            to="/trips" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Trips
          </Link>
          
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-accent" />
            <h1 className="font-display text-3xl font-bold text-foreground">Admin Dashboard</h1>
          </div>
          <p className="text-muted-foreground">
            Manage users and troubleshoot issues
          </p>

          {/* Stats Cards */}
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-accent/10 p-2 text-accent">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-card-foreground">{users?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </div>
              </div>
            </div>
            
            <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-card-foreground">
                    {users?.filter((u) => u.role === "admin").length || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Admins</p>
                </div>
              </div>
            </div>
            
            <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-muted p-2 text-muted-foreground">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-card-foreground">
                    {users?.filter((u) => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(u.created_at) > weekAgo;
                    }).length || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">New This Week</p>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="mt-8 rounded-xl border border-border bg-card shadow-soft overflow-hidden">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-card-foreground">
                All Users
              </h2>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                          {u.full_name?.charAt(0) || u.email.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium">{u.full_name || "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        {u.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.role === "admin" ? "default" : "secondary"}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {safeFormat(u.created_at, "MMM d, yyyy")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Parser Training Panel */}
          <ParserTrainingPanel />

          {/* Enrichment Debug Panel */}
          <EnrichmentDebugPanel />

          {/* Raw email debug view */}
          <RawEmailDebug />
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

function RawEmailDebug() {
  const { data: rawEmails } = useQuery({
    queryKey: ["adminRawEmails"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("raw_emails")
        .select("id, from_address, subject, created_at, processed, error_message, body_text, body_html")
        .order("created_at", { ascending: false })
        .limit(25);

      if (error) throw error;
      return data as {
        id: string;
        from_address: string;
        subject: string | null;
        created_at: string;
        processed: boolean;
        error_message: string | null;
        body_text: string | null;
        body_html: string | null;
      }[];
    },
  });

  return (
    <div className="mt-10 rounded-xl border border-border bg-card shadow-soft overflow-hidden">
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-card-foreground">
            Inbound Email Debug
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Shows the last 25 raw inbound emails, with body length and parse errors.
          </p>
        </div>
      </div>
      <div className="max-h-[420px] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Body Length</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rawEmails?.map((e) => {
              const textLen = e.body_text?.length || 0;
              const htmlLen = e.body_html?.length || 0;
              const hasContent = textLen > 0 || htmlLen > 0;

              return (
                <TableRow key={e.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {safeFormat(e.created_at, "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">
                    {e.from_address}
                  </TableCell>
                  <TableCell className="text-xs max-w-[260px] truncate">
                    {e.subject || "(no subject)"}
                  </TableCell>
                  <TableCell className="text-xs">
                    text: {textLen}, html: {htmlLen}
                  </TableCell>
                  <TableCell className="text-xs">
                    {e.processed && !e.error_message && hasContent && (
                      <span className="text-emerald-500">parsed</span>
                    )}
                    {(!hasContent || e.error_message) && (
                      <span className="text-amber-500">needs review</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
